import * as moment from 'moment';

import {ComponentTypesEnum, UnspecifiedKeysEnum} from '../../app/shared/enums';
import {FilterControlInterface} from '../../app/shared/interfaces';
import {AutocompleteControl, DatepickerControl, SelectControl} from '../../app/shared/classes';
import {ReportTableColumn, ReportTableConfigInterface} from '../../app/shared/components';

import {environment} from '../../environments/environment';

export const PanelReportsCampaignsStorageId = 'PanelReportsCampaignsComponent';

/**
 * Default window of the funnel report - the last 30 days, end date included.
 */
export const CampaignFunnelDefaultDateRangeInDays = 30;

/**
 * Filter control names. They are form control names only - the request is mapped field by field in
 * the component, so they are free to stay camelCase while the wire stays snake_case.
 */
export const CampaignFunnelDateRangeSelector = 'campaignFunnelDateRange';
export const CampaignFunnelDateFromSelector = 'campaignFunnelDateFrom';
export const CampaignFunnelDateToSelector = 'campaignFunnelDateTo';
export const CampaignFunnelCampaignSelector = 'campaignFunnelCampaign';
export const CampaignFunnelTradingPlatformSelector = 'campaignFunnelEnrollTradingPlatformId';
export const CampaignFunnelCountrySelector = 'campaignFunnelCountryId';

/**
 * The two non-campaign options of the campaign selector. Registered campaigns are carried by their
 * numeric id, so neither sentinel can ever be confused with a campaign whose code happens to read
 * "All" or "Unattributed".
 */
export enum CampaignFunnelSelectionsEnum {
    All = 'ALL',
    Unattributed = 'UNATTRIBUTED'
}

/**
 * Summary (funnel) column selectors - the snake_case field names of CampaignFunnelRowInterface.
 */
export const CampaignFunnelSummaryColumnNames = {
    CampaignCode: 'campaign_code',
    Enrolled: 'enrolled',
    KycCompleted: 'kyc_completed',
    AccountsActivated: 'accounts_activated',
    Charged: 'charged',
    StartedTrading: 'started_trading',
    AvgDaysEnrolledToCharged: 'avg_days_enrolled_to_charged'
};

/**
 * Details column selectors - the snake_case field names of CampaignFunnelClientRowInterface. They
 * are sent verbatim as `order_column` and as `corresponding_to_response_model` on export, so every
 * one of them must stay a real field of that response row.
 *
 * Identifiers only: the funnel drill-down carries no name, email or phone, and none may be added.
 */
export const CampaignFunnelDetailsColumnNames = {
    EnrollDate: 'enroll_date',
    EnrollType: 'enroll_type',
    EnrollId: 'enroll_id',
    CampaignCode: 'campaign_code',
    CountryId: 'country_id',
    PortalName: 'portal_name',
    PlatformName: 'platform_name',
    KycCompletedOn: 'kyc_completed_on',
    AccountActivatedOn: 'account_activated_on',
    AnyChargeOn: 'any_charge_on',
    StartedTradingOn: 'started_trading_on',
    UtmSource: 'utm_source',
    UtmMedium: 'utm_medium',
    Gclid: 'gclid'
};

export const PanelReportsCampaignsFilterControls = (): FilterControlInterface[] => {
    const today = moment();
    const dateFrom = moment().subtract(CampaignFunnelDefaultDateRangeInDays - 1, 'days');

    return [
        {
            name: CampaignFunnelDateRangeSelector,
            placeholder: 'Enroll date',
            controls: [
                new DatepickerControl({
                    name: CampaignFunnelDateFromSelector,
                    value: dateFrom.format(environment.dateFormatLocalDate),
                    placeholder: 'Enroll date from',
                    maxDate: today.format(environment.dateFormatLocalDate),
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 1
                }),
                new DatepickerControl({
                    name: CampaignFunnelDateToSelector,
                    value: today.format(environment.dateFormatLocalDate),
                    placeholder: 'Enroll date to',
                    maxDate: today.format(environment.dateFormatLocalDate),
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 2
                })
            ],
            shown: true,
            sortIndex: 1
        },
        {
            name: CampaignFunnelCampaignSelector,
            placeholder: 'Campaign',
            controls: [
                new SelectControl({
                    name: CampaignFunnelCampaignSelector,
                    value: CampaignFunnelSelectionsEnum.All,
                    placeholder: 'Campaign',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    // The registered campaigns are appended in the resolver from the campaign registry.
                    options: [
                        {
                            label: 'All',
                            value: CampaignFunnelSelectionsEnum.All
                        },
                        {
                            label: 'Unattributed',
                            value: CampaignFunnelSelectionsEnum.Unattributed
                        }
                    ]
                })
            ],
            shown: true,
            sortIndex: 2
        },
        {
            name: CampaignFunnelTradingPlatformSelector,
            placeholder: 'Initial Platform',
            controls: [
                new SelectControl({
                    name: CampaignFunnelTradingPlatformSelector,
                    value: UnspecifiedKeysEnum.UnspecifiedKey,
                    placeholder: 'Initial Platform',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    // Options are populated in the resolver from the trading platforms list.
                    options: [
                        {
                            label: 'All',
                            value: UnspecifiedKeysEnum.UnspecifiedKey
                        }
                    ]
                })
            ],
            shown: true,
            sortIndex: 3
        },
        {
            name: CampaignFunnelCountrySelector,
            placeholder: 'Country',
            controls: [
                new AutocompleteControl({
                    name: CampaignFunnelCountrySelector,
                    value: null,
                    placeholder: 'Country',
                    validators: [],
                    componentType: ComponentTypesEnum.Autocomplete,
                    // Options are populated in the resolver from the countries list.
                    options: []
                })
            ],
            shown: true,
            sortIndex: 4
        }
    ];
};

/**
 * Funnel summary strip - one line per returned row. The stage cells carry the count and the
 * client-side conversion percentage; the component renders the em dash where a percentage cannot be
 * computed (no enrolments) or where the stage itself is null.
 */
export const PanelReportsCampaignFunnelSummaryDataTableConfig = (): ReportTableConfigInterface => {
    return {
        rows: [],
        columns: [
            new ReportTableColumn({
                label: 'Campaign',
                selector: CampaignFunnelSummaryColumnNames.CampaignCode
            }),
            new ReportTableColumn({
                label: 'Enrolled',
                selector: CampaignFunnelSummaryColumnNames.Enrolled,
                shifted: true
            }),
            new ReportTableColumn({
                label: 'KYC Completed',
                selector: CampaignFunnelSummaryColumnNames.KycCompleted,
                shifted: true
            }),
            new ReportTableColumn({
                label: 'Accounts Activated',
                selector: CampaignFunnelSummaryColumnNames.AccountsActivated,
                shifted: true
            }),
            new ReportTableColumn({
                label: 'Charged',
                selector: CampaignFunnelSummaryColumnNames.Charged,
                shifted: true
            }),
            new ReportTableColumn({
                label: 'Started Trading',
                selector: CampaignFunnelSummaryColumnNames.StartedTrading,
                shifted: true
            }),
            new ReportTableColumn({
                label: 'Avg Days Enrolled To Charged',
                selector: CampaignFunnelSummaryColumnNames.AvgDaysEnrolledToCharged,
                shifted: true
            })
        ],
        hiddenColumnIds: [],
        actionMenu: false,
        disableColumnsOptions: true
    };
};

/**
 * Drill-down table of the clients behind one funnel row. Every selector is a snake_case field of
 * CampaignFunnelClientRowInterface because it doubles as the sort key (`order_column`) and as the
 * export's `corresponding_to_response_model`.
 *
 * The platform column is bound to the enrolment platform pair (`platform_name` /
 * `enroll_trading_platform_id`), never to the portal; the portal has its own column.
 * `started_trading_on` is null for the whole of this release, so it is not offered as a sort key.
 */
export const PanelReportsCampaignFunnelDetailsDataTableConfig = (): ReportTableConfigInterface => {
    return {
        rows: [],
        columns: [
            new ReportTableColumn({
                label: 'Enroll Date',
                selector: CampaignFunnelDetailsColumnNames.EnrollDate,
                sortable: true
            }),
            new ReportTableColumn({
                label: 'Client Type',
                selector: CampaignFunnelDetailsColumnNames.EnrollType,
                sortable: true
            }),
            new ReportTableColumn({
                label: 'Enroll ID',
                selector: CampaignFunnelDetailsColumnNames.EnrollId,
                sortable: true
            }),
            new ReportTableColumn({
                label: 'Campaign',
                selector: CampaignFunnelDetailsColumnNames.CampaignCode,
                sortable: true
            }),
            new ReportTableColumn({
                label: 'Country',
                selector: CampaignFunnelDetailsColumnNames.CountryId,
                sortable: true
            }),
            new ReportTableColumn({
                label: 'Portal',
                selector: CampaignFunnelDetailsColumnNames.PortalName,
                sortable: true
            }),
            new ReportTableColumn({
                label: 'Initial Platform',
                selector: CampaignFunnelDetailsColumnNames.PlatformName,
                sortable: true
            }),
            new ReportTableColumn({
                label: 'KYC Completed',
                selector: CampaignFunnelDetailsColumnNames.KycCompletedOn,
                sortable: true
            }),
            new ReportTableColumn({
                label: 'Account Activated',
                selector: CampaignFunnelDetailsColumnNames.AccountActivatedOn,
                sortable: true
            }),
            new ReportTableColumn({
                label: 'First Charge',
                selector: CampaignFunnelDetailsColumnNames.AnyChargeOn,
                sortable: true
            }),
            new ReportTableColumn({
                label: 'Started Trading',
                selector: CampaignFunnelDetailsColumnNames.StartedTradingOn
            }),
            new ReportTableColumn({
                label: 'UTM Source',
                selector: CampaignFunnelDetailsColumnNames.UtmSource,
                sortable: true
            }),
            new ReportTableColumn({
                label: 'UTM Medium',
                selector: CampaignFunnelDetailsColumnNames.UtmMedium,
                sortable: true
            }),
            new ReportTableColumn({
                label: 'GCLID',
                selector: CampaignFunnelDetailsColumnNames.Gclid,
                sortable: true
            })
        ],
        hiddenColumnIds: [],
        actionMenu: false,
        disableColumnsOptions: true
    };
};
